/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { of, throwError } from "rxjs";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import { isNullOrUndefined } from "../../../utils";
import {
	GET_REALTIME_CARBON_INTENSITY_RESPONSE,
	GET_REALTIME_EMBODIED_EMISSIONS_RESPONSE,
	GET_REALTIME_EMISSIONS_BY_LOCATION_RESPONSE,
	GET_REALTIME_EMISSIONS_BY_MODEL_PER_DEVICE_RESPONSE,
	GET_REALTIME_EMISSIONS_BY_MODEL_TOTAL_RESPONSE,
	GET_REALTIME_ENERGY_CONSUMPTION_RESPONSE,
	GET_REALTIME_INFORMATION_RESPONSE,
	GET_REALTIME_MANUFACTURERS_EMISSION_RESPONSE,
	GET_REALTIME_PIE_CHART_CO2_RESPONSE,
	GET_REALTIME_TOTAL_EMISSIONS_RESPONSE
} from "./dashboard-hardware-assets-realtime.db";

export class DashboardHardwareAssetsRealtime {
	public static getInformations(options: HttpRequestOptions) {
		const { params } = options;
		const httpParams = params as HttpParams;

		if (!httpParams) {
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getInformations.name
					)
			);
		}

		const startDate = httpParams.get("startDate");

		if (!startDate) {
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getInformations.name
					)
			);
		}

		return of(GET_REALTIME_INFORMATION_RESPONSE);
	}

	public static getTotalEmissions(options: HttpRequestOptions) {
		const { params } = options;
		const httpParams = params as HttpParams;

		if (!httpParams) {
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalEmissions.name
					)
			);
		}

		const startDate = httpParams.get("startDate");

		if (!startDate) {
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalEmissions.name
					)
			);
		}

		return of(GET_REALTIME_TOTAL_EMISSIONS_RESPONSE);
	}

	public static getEmissionsByModelTotal(options: HttpRequestOptions) {
		const { params } = options;
		const httpParams = params as HttpParams;

		if (!httpParams) {
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getEmissionsByModelTotal.name
					)
			);
		}

		const startDate = httpParams.get("startDate");

		if (!startDate) {
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getEmissionsByModelTotal.name
					)
			);
		}

		return of(GET_REALTIME_EMISSIONS_BY_MODEL_TOTAL_RESPONSE);
	}

	public static getEmissionsByModelPerDevice(options: HttpRequestOptions) {
		const { params } = options;
		const httpParams = params as HttpParams;

		if (!httpParams) {
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getEmissionsByModelPerDevice.name
					)
			);
		}

		const startDate = httpParams.get("startDate");

		if (!startDate) {
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getEmissionsByModelPerDevice.name
					)
			);
		}

		return of(GET_REALTIME_EMISSIONS_BY_MODEL_PER_DEVICE_RESPONSE);
	}

	public static getEmissionsByLocation(options: HttpRequestOptions) {
		const { params } = options;
		const httpParams = params as HttpParams;

		if (isNullOrUndefined(httpParams)) {
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getEmissionsByLocation.name
					)
			);
		}

		return of(GET_REALTIME_EMISSIONS_BY_LOCATION_RESPONSE);
	}

	public static getManufacturersEmission() {
		return of(GET_REALTIME_MANUFACTURERS_EMISSION_RESPONSE);
	}

	public static getManufacturerPieChart() {
		return of(GET_REALTIME_PIE_CHART_CO2_RESPONSE);
	}

	public static getCarbonIntensityMap() {
		return of(GET_REALTIME_CARBON_INTENSITY_RESPONSE);
	}

	public static getEnergyConsumption() {
		return of(GET_REALTIME_ENERGY_CONSUMPTION_RESPONSE);
	}

	public static getEmbodiedEmissions() {
		return of(GET_REALTIME_EMBODIED_EMISSIONS_RESPONSE);
	}
}
